import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Coins, BadgeCheck, ClipboardCheck, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HERO } from '@/constants/testIds';
import { useI18n } from '@/i18n/I18nProvider';
import { BRAND } from '@/lib/brandAssets';
import { FADE_UP, STAGGER_CONTAINER, SOFT_EASE } from '@/lib/motion';

// Icons map 1:1 to the hero.pillars array order:
// 1) Assess  2) Connect  3) Finance  4) Verify
const PILLAR_ICONS = [ClipboardCheck, Users, Coins, BadgeCheck];
const HERO_VISUAL_INITIAL = { opacity: 0, y: 18 };
const HERO_VISUAL_ANIMATE = { opacity: 1, y: 0 };
const HERO_VISUAL_TRANSITION = { delay: 0.2, duration: 0.7, ease: SOFT_EASE };
const HERO_CHIP_INITIAL = { opacity: 0, y: 8 };
const HERO_CHIP_ANIMATE = { opacity: 1, y: 0 };
const HERO_CHIP_TRANSITION = { delay: 0.6, duration: 0.6 };
const HERO_VISUAL_OVERLAY_BG = {
  background:
    'linear-gradient(180deg, rgba(8,16,12,0.15) 0%, rgba(8,16,12,0) 30%, rgba(8,16,12,0.55) 60%, rgba(8,16,12,0.95) 78%, rgb(8,16,12) 86%, rgb(8,16,12) 100%)',
};

export default function Hero() {
  const { t, m } = useI18n();
  const goTo = (id) => (e) => {
    e?.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="hero"
      data-testid={HERO.root}
      className="relative overflow-hidden bg-forest noise-overlay"
    >
      <div className="hero-aurora" aria-hidden />
      <div className="hero-grid-lines opacity-60" aria-hidden />
      <div className="orb" aria-hidden
        style={{ width: 460, height: 460, background: 'hsla(86, 70%, 60%, 0.22)', top: -160, left: -150 }} />
      <div className="orb" aria-hidden
        style={{ width: 340, height: 340, background: 'hsla(150, 45%, 38%, 0.28)', top: -80, right: -100 }} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24">
        <motion.div variants={STAGGER_CONTAINER} initial="hidden" animate="show"
          className="grid gap-10 md:gap-14 md:grid-cols-[1.15fr_0.85fr] items-center">

          {/* LEFT: copy */}
          <div className="max-w-2xl">
            <motion.h1
              variants={FADE_UP}
              data-testid={HERO.headline}
              className="font-display text-[38px] sm:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.025em] hyphens-auto"
            >
              <span className="block">{m.hero.headlineA}</span>
              <span className="block text-primary">{m.hero.headlineB}</span>
            </motion.h1>

            <motion.p
              variants={FADE_UP}
              data-testid={HERO.sub}
              className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl"
            >
              {m.hero.sub}
            </motion.p>

            <motion.div variants={FADE_UP} className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                onClick={goTo('waitlist')}
                data-testid={HERO.ctaWaitlist}
                className="h-11 px-5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
              >
                {m.hero.ctaPrimary}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button
                onClick={goTo('solution')}
                data-testid={HERO.ctaHowItWorks}
                variant="secondary"
                className="h-11 px-5 rounded-xl bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border"
              >
                {m.hero.ctaSecondary}
              </Button>
            </motion.div>

            <motion.div variants={FADE_UP} className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-accent" />
              <span>{t('hero.privacyNote')}</span>
            </motion.div>

            {/* 4 pillars */}
            <motion.div variants={FADE_UP} className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {m.hero.pillars.map((p, i) => {
                const Icon = PILLAR_ICONS[i] || Coins;
                return (
                  <div
                    key={p.k}
                    className="rounded-xl border border-border bg-secondary/40 p-3 transition-colors hover:border-primary/30 min-w-0"
                  >
                    <div className="flex items-center gap-1.5 text-primary">
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="text-[10px] uppercase tracking-[0.18em] font-medium truncate">
                        {p.k}
                      </span>
                    </div>
                    {p.t && (
                      <div className="mt-1.5 text-[12px] font-medium leading-snug text-foreground break-words hyphens-auto">
                        {p.t}
                      </div>
                    )}
                    <div className="mt-1 text-[11px] leading-snug text-foreground/70 break-words hyphens-auto">
                      {p.v}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </div>

          {/* RIGHT: real brand visual (bottle + Impact Passport phone) */}
          <motion.div
            variants={FADE_UP}
            className="relative"
            initial={HERO_VISUAL_INITIAL}
            animate={HERO_VISUAL_ANIMATE}
            transition={HERO_VISUAL_TRANSITION}
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-border glow-lime brand-img-wrap bg-secondary/30">
              <img
                src={BRAND.cover}
                alt="Klymart — Verified Impact product passport"
                className="brand-img-right"
                draggable={false}
              />
              {/* Subtle overlay to bind the image to brand tones — strong mask at bottom
                  fully hides any baked-in slide caption/branding. */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={HERO_VISUAL_OVERLAY_BG}
              />
              {/* Floating top-right chip */}
              <motion.div
                initial={HERO_CHIP_INITIAL}
                animate={HERO_CHIP_ANIMATE}
                transition={HERO_CHIP_TRANSITION}
                className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/15 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-wider text-accent"
              >
                <ShieldCheck className="h-3 w-3" />
                {m.hero.tagBottle}
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
